import { NavigateOptions, To, useNavigate } from 'react-router-dom';

import { generatePath } from './url';
import { globaAppContextState } from '@/contexts/app-context';

/**
 * Ensures the path has exactly one env parameter with the current environment value.
 * If the env parameter exists, it will be replaced. If it doesn't exist, it will be added.
 * @param path - The URL path to set the env parameter on
 * @returns The path with the env parameter set to the current environment
 */
export const appendEnvParam = (path: string): string => {
  const env = globaAppContextState.environment;
  const [pathname, queryString] = path.split('?');

  // No query string yet → just add env
  if (!queryString) {
    return `${pathname}?env=${env}`;
  }

  // Parse, set/replace env, then reconstruct
  const params = new URLSearchParams(queryString);
  params.set('env', env);
  const nextQuery = params.toString();

  return nextQuery ? `${pathname}?${nextQuery}` : `${pathname}`;
};

/**
 * Type for navigation parameters
 */
export interface NavigationParams {
  path: string;
  params?: Record<string, any>;
  options?: NavigateOptions;
}

/**
 * Custom hook that provides a wrapped version of useNavigate with additional functionality
 * @returns A function that can be used for navigation with path parameter support
 */
export const useAppNavigate = () => {
  const navigate = useNavigate();

  return {
    /**
     * Navigate to a route with support for path parameters and search params
     * @param to - Either a string path or a NavigationParams object
     * @param options - Optional navigation options (when first param is string)
     */
    navigateTo: (to: To | NavigationParams, options?: NavigateOptions) => {
      // If to is a string, append env param and use it directly with options
      if (typeof to === 'string') {
        navigate(appendEnvParam(to), options);
        return;
      }

      // Check if to is a NavigationParams object
      if ('path' in to) {
        // If to is a NavigationParams object, process it
        const {
          path,
          params,
          options: navigationOptions,
        } = to as NavigationParams;

        // Generate the final path with parameters if they exist
        const finalPath = params ? generatePath(path, params) : path;

        // Append env param and navigate to the processed path
        navigate(appendEnvParam(finalPath), navigationOptions);
        return;
      }

      // Handle Location object case
      if (typeof to.pathname === 'string') {
        navigate(
          {
            ...to,
            pathname: appendEnvParam(to.pathname),
          },
          options,
        );
        return;
      }

      // Fallback for any other case
      navigate(to, options);
    },

    /**
     * Navigate back in history
     */
    goBack: () => {
      navigate(-1);
    },

    /**
     * Replace the current URL
     * @param to - Either a string path or a NavigationParams object
     * @param options - Optional navigation options (when first param is string)
     */
    replaceTo: (to: To | NavigationParams, options?: NavigateOptions) => {
      if (typeof to === 'string') {
        navigate(appendEnvParam(to), { ...options, replace: true });
        return;
      }

      if ('path' in to) {
        const { path, params, options: toOptions } = to as NavigationParams;
        const finalPath = params ? generatePath(path, params) : path;
        navigate(appendEnvParam(finalPath), { ...toOptions, replace: true });
        return;
      }

      // Handle Location object case
      if (typeof to.pathname === 'string') {
        navigate(
          {
            ...to,
            pathname: appendEnvParam(to.pathname),
          },
          { ...options, replace: true },
        );
        return;
      }

      // Fallback for any other case
      navigate(to, { ...options, replace: true });
    },
  };
};
